import React, { useState, useEffect } from 'react';
import { X, Trash2, Bookmark, ChevronRight } from 'lucide-react';
import type { SavedRouteEntry } from '../types';

type SavedRoutesDrawerProps = {
  routes: SavedRouteEntry[];
  onSelectRoute: (entry: SavedRouteEntry) => void;
  onDeleteRoute: (id: string) => void;
  onClose: () => void;
};

export const SavedRoutesDrawer: React.FC<SavedRoutesDrawerProps> = ({
  routes: initialRoutes,
  onSelectRoute,
  onDeleteRoute,
  onClose,
}) => {
  const [routes, setRoutes] = useState(initialRoutes);

  useEffect(() => {
    setRoutes(initialRoutes);
  }, [initialRoutes]);

  const handleDelete = (id: string) => {
    setRoutes((curr) => curr.filter((r) => r.id !== id));
    onDeleteRoute(id);
  };
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 980,
        backgroundColor: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="sheet-surface"
        style={{
          width: '100%',
          maxWidth: 500,
          borderTopLeftRadius: 36,
          borderTopRightRadius: 36,
          padding: '16px 20px 32px',
          boxSizing: 'border-box',
          maxHeight: '80vh',
          overflowY: 'auto',
          color: 'var(--label)',
        }}
      >
        {/* Grabber */}
        <div
          style={{
            width: 36,
            height: 5,
            borderRadius: 2.5,
            backgroundColor: 'var(--label3)',
            margin: '0 auto 12px',
          }}
        />

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 16,
                backgroundColor: 'rgba(0,122,255,0.15)',
                color: 'var(--accent)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Bookmark size={16} />
            </div>
            <div>
              <div className="t-title2">Saved on this browser</div>
              <div className="t-foot c2">{routes.length} origin–destination pair{routes.length === 1 ? '' : 's'}</div>
            </div>
          </div>
          <button className="cbtn" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {/* Routes List */}
        {routes.length === 0 ? (
          <div className="card-surface" style={{ padding: 24, textAlign: 'center' }}>
            <div className="t-headline">No saved pairs</div>
            <div className="t-foot c2" style={{ marginTop: 4 }}>
              Save an origin and destination on this browser for quick reopen.
            </div>
          </div>
        ) : (
          <div className="cell-group">
            {routes.map((entry) => {
              const startShort = entry.start.displayName.split(',')[0] || entry.start.displayName;
              const endShort = entry.end.displayName.split(',')[0] || entry.end.displayName;
              const dateStr = new Date(entry.createdAt).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
              });

              return (
                <div
                  key={entry.id}
                  className="cell"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    onSelectRoute(entry);
                    onClose();
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="t-headline" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span>{startShort}</span>
                      <span className="c3">→</span>
                      <span>{endShort}</span>
                    </div>
                    <div className="t-foot c2" style={{ marginTop: 2 }}>
                      This browser · {dateStr}
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button
                      className="cbtn"
                      style={{ width: 32, height: 32, color: 'var(--danger)' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(entry.id);
                      }}
                      aria-label="Delete saved route"
                    >
                      <Trash2 size={15} />
                    </button>
                    <ChevronRight size={18} color="var(--label3)" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
